/**
 * ThermalPrintService
 * 
 * Serviço para impressão em impressoras térmicas Bematech MP 4200
 * usando comandos ESC/POS através de WebUSB ou plugin de impressão.
 * 
 * Funcionalidades:
 * - Detecção automática da impressora
 * - Envio de comandos ESC/POS
 * - Suporte a impressão via WebUSB (Chrome/Edge)
 * - Fallback para impressão via window.print()
 */

class ThermalPrintService {
  constructor() {
    this.device = null;
    this.connected = false;
  }

  /**
   * Verifica se o navegador suporta WebUSB
   */
  isWebUSBSupported() {
    return 'usb' in navigator;
  }

  /**
   * Solicita permissão e conecta à impressora Bematech MP 4200
   * Vendor ID: 0x0B1B (Bematech)
   * Product ID: 0x0003 (MP 4200 TH)
   */
  async conectar() {
    if (!this.isWebUSBSupported()) {
      throw new Error('Seu navegador não suporta WebUSB. Use Chrome ou Edge.');
    }

    try {
      // Solicitar dispositivo USB
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: 0x0B1B, productId: 0x0003 }, // Bematech MP 4200 TH
          { vendorId: 0x0B1B } // Qualquer Bematech
        ]
      });

      await device.open();
      
      // Selecionar configuração (geralmente a primeira)
      if (device.configuration === null) {
        await device.selectConfiguration(1);
      }

      // Reivindicar interface (geralmente a primeira)
      await device.claimInterface(0);

      this.device = device;
      this.connected = true;

      console.log('Impressora conectada:', device.productName || 'Bematech');
      
      return true;
    } catch (error) {
      console.error('Erro ao conectar impressora:', error);
      throw new Error('Falha ao conectar com a impressora. Verifique se está ligada e conectada via USB.');
    }
  }

  /**
   * Desconecta da impressora
   */
  async desconectar() {
    if (this.device) {
      try {
        await this.device.close();
        this.device = null;
        this.connected = false;
        console.log('Impressora desconectada');
      } catch (error) {
        console.error('Erro ao desconectar:', error);
      }
    }
  }

  /**
   * Envia dados para a impressora
   * @param {string} data - Dados com comandos ESC/POS
   */
  async enviarDados(data) {
    if (!this.connected || !this.device) {
      throw new Error('Impressora não conectada');
    }

    try {
      // Converter string para array de bytes
      const encoder = new TextEncoder();
      const bytes = encoder.encode(data);

      // Enviar dados para o endpoint 1 (geralmente o endpoint de saída)
      await this.device.transferOut(1, bytes);

      console.log('Dados enviados para impressora');
      return true;
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
      throw new Error('Falha ao enviar dados para a impressora');
    }
  }

  /**
   * Imprime cupom com comandos ESC/POS
   * @param {string} comandosESC - String com comandos ESC/POS e texto
   */
  async imprimir(comandosESC) {
    try {
      // Tentar conectar se não estiver conectado
      if (!this.connected) {
        await this.conectar();
      }

      // Enviar comandos para impressora
      await this.enviarDados(comandosESC);

      return true;
    } catch (error) {
      console.error('Erro na impressão:', error);
      throw error;
    }
  }

  /**
   * Imprime texto simples (fallback quando WebUSB não está disponível)
   * Usa window.print() com formatação pré-definida
   * @param {string} texto - Texto formatado para impressão
   */
  imprimirTextoSimples(texto) {
    // Criar janela de impressão
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    if (!printWindow) {
      throw new Error('Popup bloqueado. Permita popups para imprimir.');
    }

    // HTML formatado para impressão térmica
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Impressão de Cupom</title>
          <style>
            @page {
              size: 80mm auto;
              margin: 0;
            }
            
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              margin: 0;
              padding: 5mm;
              width: 80mm;
            }
            
            pre {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              margin: 0;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <pre>${texto.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 500);
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  }

  /**
   * Testa a impressora com texto de teste
   */
  async testar() {
    const textoTeste = `
========================================
           TESTE DE IMPRESSAO
========================================

Impressora: Bematech MP 4200

Data/Hora: ${new Date().toLocaleString('pt-BR')}

Status: OK

========================================
    `;

    try {
      if (this.isWebUSBSupported()) {
        await this.imprimir(textoTeste);
      } else {
        this.imprimirTextoSimples(textoTeste);
      }
      return true;
    } catch (error) {
      console.error('Erro no teste:', error);
      throw error;
    }
  }
}

// Criar instância singleton
const thermalPrintService = new ThermalPrintService();

export default thermalPrintService;

